import {
  Box,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  InputLabel,
  Divider,
  Table,
  TableFooter,
} from '@mui/material';
import { useState, useMemo, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';

import { formatCurrency, handleApiError } from '~/utils/helper';
import { ILeadReport } from '~/utils/interface/report';
import { HeadCellType } from '~/utils/types/common';
import { useTranslation } from 'react-i18next';
import { BaseDatePicker, BaseHeaderTitle, BaseRow, BaseSelect } from '~/components/common';
import dayjs from 'dayjs';
import { handleGetAccountsSelection } from '~/thunks/account/accountThunk';
import { handleGetLeadReport } from '~/thunks/report/reportThunk';

const LeadReport = () => {
  const { t } = useTranslation('admin/report');
  const dispatch = useAppDispatch();

  const currentDate = new Date();
  const end = currentDate.toISOString().split('T')[0];
  const start = new Date(currentDate.setMonth(currentDate.getMonth() - 1)).toISOString().split('T')[0];
  const headCells: Array<HeadCellType> = useMemo(
    () => [
      { id: 'projectId', label: t('properties.projectId') },
      { id: 'projectStatus', label: t('properties.projectStatus') },
      { id: 'clientName', label: t('properties.clientName') },
      { id: 'referralSource', label: t('properties.referralSource') },
      { id: 'estimatedAmount', label: t('properties.estimatedAmount') },
      { id: 'invoiceAmount', label: t('properties.invoiceAmount') },
    ],
    [t]
  );

  const { selectionAccounts } = useAppSelector((state) => state.account);
  const [startDate, setStartDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
  const [total, setTotal] = useState<number>();
  const [selectedAccount, setSelectedAccount] = useState<Array<string>>([]); // State for selected account
  const [job, setJob] = useState<Array<ILeadReport>>([]);
  const optionsWithAll = [{ id: 'all', name: 'All' }, ...selectionAccounts];

  const handleSearch = () => {
    // Dispatch the request to fetch the activity report with the selected dates and account filter
    if (selectedAccount.includes('all')) {
      dispatch(handleGetLeadReport({ startDate, endDate }))
        .unwrap()
        .then((response) => {
          setJob(response.responses || []);
          setTotal(response.result.count);
        })
        .catch(handleApiError);
    } else {
      dispatch(handleGetLeadReport({ startDate, endDate, createdBy: selectedAccount ?? [] }))
        .unwrap()
        .then((response) => {
          setJob(response.responses || []);
          setTotal(response.result.count);
        })
        .catch(handleApiError);
    }
  };

  useEffect(() => {
    dispatch(handleGetAccountsSelection({}))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    dispatch(handleGetLeadReport({ startDate: start, endDate: end }))
      .unwrap()
      .then((response) => {
        setStartDate(start);
        setEndDate(end);
        setTotal(response.result.count);
        setSelectedAccount(['all']);
        setJob(response.responses || []);
      })
      .catch(handleApiError);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <BaseHeaderTitle title={t('title.job')} />

      <Divider className='mb-[20px] mt-[20px]' />
      <BaseRow className='gap-[20px] '>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.startDate')}</InputLabel>
          <BaseDatePicker
            value={startDate}
            onChange={(value) => setStartDate(dayjs(value).format('YYYY-MM-DD'))}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-[900px]'>
          <InputLabel>{t('properties.endDate')}</InputLabel>
          <BaseDatePicker
            value={endDate}
            onChange={(value) => setEndDate(dayjs(value).format('YYYY-MM-DD'))}
            dateFormat='DD MMM, YYYY'
          />
        </Box>
        <Box className='w-full'>
          <InputLabel>{t('properties.createdBy')}</InputLabel>
          <BaseSelect
            options={optionsWithAll}
            value={selectedAccount}
            onChange={(event) => {
              const selectedValue = event.target.value;

              if (selectedValue.includes('all') && !selectedAccount.includes('all')) {
                setSelectedAccount(['all']);
              } else if (selectedValue !== 'all' && selectedAccount.includes('all')) {
                setSelectedAccount(selectedValue.filter((value: any) => value !== 'all'));
              } else {
                setSelectedAccount(selectedValue);
              }
            }}
            label={t('properties.createdBy')}
            placeholder={t('properties.createdBy')}
            multiple
          />
        </Box>
        <Box className='w-[200px] mt-[12px]'>
          <InputLabel></InputLabel>
          <Button variant='contained' color='primary' onClick={handleSearch}>
            {t('search')}
          </Button>
        </Box>
      </BaseRow>

      <Divider className='mb-[20px] mt-[20px]' />
      <TableContainer className='max-h-[640px] overflow-auto'>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {headCells.map((cell) => (
                <TableCell key={cell.id} className='w-[16.66%] bg-[#F0F7FF] text-[#758CA5] text-lg font-bold'>
                  {cell.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {job.map((row, index) => (
              <TableRow key={index}>
                <TableCell className='w-[16.66%]'>{row.name}</TableCell>
                <TableCell className='w-[16.66%]'>{row.statusId.name}</TableCell>
                <TableCell className='w-[16.66%]'>{row.customerName}</TableCell>
                <TableCell className='w-[16.66%]'>{row.referralSource}</TableCell>
                <TableCell className='w-[16.66%]'>{formatCurrency(row.totalEstimates)}</TableCell>
                <TableCell className='w-[16.66%]'>{formatCurrency(row.totalInvoices)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          {/* Total row */}
          <TableFooter>
            <TableRow className='sticky bottom-0 bg-[#F0F7FF]'>
              <TableCell className='w-[16.66%] font-bold text-[#758CA5]'>{total}</TableCell>
              <TableCell colSpan={3} className='w-[50.02%] font-bold text-right text-[#758CA5]'></TableCell>
              <TableCell className='w-[16.66%] font-bold text-[#758CA5]'>
                {formatCurrency(job.reduce((sum, row) => sum + (row.totalEstimates || 0), 0))}
              </TableCell>
              <TableCell className='w-[16.66%] font-bold text-[#758CA5]'>
                {formatCurrency(job.reduce((sum, row) => sum + (row.totalInvoices || 0), 0))}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default LeadReport;
